import React from 'react';
import { Badge } from '@fluentui/react-components';
import { useGetLinkedProductsQuery, useGetStorefrontQuery } from '../../Api/Api';
import { ILinkedProduct, IProduct } from '../../Api/Models/Products';
import ProductCard from '../Cards/ProductCard';
import CAN, { PRICING } from '../Auth/Permissions';
import { parseProductPageConfig } from '../Helpers/productPageConfig';
import { elementStyleFrom } from '../Helpers/searchPageConfig';

interface ILinkedProductsProps {
    productId?: string;
}

// Friendly headings for the well-known linked-product types. Anything else falls
// back to a title-cased version of the raw type string.
const DEFAULT_TYPE_LABELS: Record<string, string> = {
    services: 'Services & Warranties',
    accessories: 'Accessories',
    alternatives: 'Alternatives',
    replacements: 'Replacements',
};

// Section ordering: services, then accessories, then any custom types
// (alphabetically), then alternatives, with replacements always last.
const HEAD_ORDER = ['services', 'accessories'];
const TAIL_ORDER = ['alternatives', 'replacements'];

const titleCase = (value: string): string =>
    value
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

const labelForType = (type: string): string => DEFAULT_TYPE_LABELS[type] ?? titleCase(type);

const orderGroupKeys = (keys: string[]): string[] => {
    const head = HEAD_ORDER.filter(k => keys.includes(k));
    const tail = TAIL_ORDER.filter(k => keys.includes(k));
    const custom = keys
        .filter(k => !HEAD_ORDER.includes(k) && !TAIL_ORDER.includes(k))
        .sort((a, b) => a.localeCompare(b));
    return [...head, ...custom, ...tail];
};

const LinkedProducts: React.FC<ILinkedProductsProps> = ({ productId }) => {
    const canSeePricing = CAN(PRICING);
    const linkedProducts = useGetLinkedProductsQuery({ productId: productId ?? '' }, { skip: !productId });

    // Merchant styling for this section comes from the same productPageConfig JSON blob the rest
    // of the product page uses (linked* sections, edited in the admin Product Pages editor).
    const sfSettings = useGetStorefrontQuery();
    const config = React.useMemo(
        () => parseProductPageConfig(sfSettings.data?.productPageConfig),
        [sfSettings.data?.productPageConfig]
    );
    const showAddToCart = config.linkedShowAddToCart !== false;

    if (linkedProducts.isLoading || linkedProducts.error) return null;
    const data = linkedProducts.data;
    if (!data || data.length === 0) return null;

    const groups = new Map<string, ILinkedProduct[]>();
    data.forEach(item => {
        const list = groups.get(item.type) ?? [];
        list.push(item);
        groups.set(item.type, list);
    });

    // Recommended entries sort first within their group; ordering is otherwise stable.
    groups.forEach(list => list.sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0)));

    const orderedKeys = orderGroupKeys(Array.from(groups.keys()));
    if (orderedKeys.length === 0) return null;

    return (
        <div style={{ marginTop: '2em', ...elementStyleFrom(config.linkedSection) }}>
            {orderedKeys.map(type => {
                const items = groups.get(type) ?? [];
                if (items.length === 0) return null;
                return (
                    <div key={type} style={{ marginBottom: '1.5em' }}>
                        <h2 style={{ lineHeight: 1.3, ...elementStyleFrom(config.linkedHeading, 'heading') }}>{labelForType(type)}</h2>
                        <div className="linked-products-scroll-row" style={elementStyleFrom(config.linkedRow)}>
                            {items.map(item => {
                                // The backing endpoint already nulls out price when the viewer
                                // can't see pricing, but gate defensively on the permission too
                                // without touching ProductCard's own price-display logic.
                                const cardProduct: IProduct = canSeePricing
                                    ? item.product
                                    : { ...item.product, price: undefined };
                                return (
                                    <div
                                        key={`${type}-${item.product.id}`}
                                        className="linked-product-card-small"
                                        style={{ position: 'relative' }}
                                    >
                                        {item.recommended && (
                                            <Badge
                                                appearance="filled"
                                                color="brand"
                                                size="small"
                                                style={{ position: 'absolute', top: 6, right: 6, zIndex: 5, ...elementStyleFrom(config.linkedRecommendedBadge) }}
                                            >
                                                Recommended
                                            </Badge>
                                        )}
                                        <ProductCard
                                            product={cardProduct}
                                            showAddToCart={showAddToCart}
                                            showFeaturedFlag={false}
                                            cardStyle={elementStyleFrom(config.linkedCard, 'card')}
                                            source="product"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default LinkedProducts;
